import React, { useMemo } from "react";
import { Box, Typography, LinearProgress, Chip, useTheme } from "@mui/material";
import { evaluatePasswordStrength, PasswordStrength } from "../../utils/passwordStrength";
import { isPasswordCompliant } from "../../utils/passwordPolicy";

interface PasswordStrengthMeterProps {
  password: string;
  showSuggestions?: boolean;
  maxSuggestions?: number;
  onStrengthChange?: (strength: PasswordStrength) => void;
}

const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({
  password,
  showSuggestions = true,
  maxSuggestions = 3,
  onStrengthChange,
}) => {
  const theme = useTheme();

  const strength = useMemo(() => evaluatePasswordStrength(password), [password]);
  let strengthPercent = (strength.score / 4) * 100;
  const compliant = isPasswordCompliant(password);
  // Ensure 100% only when policy is met
  if (!compliant && strengthPercent >= 100) {
    strengthPercent = 95;
  }
  const strengthColor =
    strength.score <= 1
      ? theme.palette.error.main
      : strength.score === 2
      ? theme.palette.warning.main
      : strength.score === 3
      ? theme.palette.info.main
      : theme.palette.success.main;

  React.useEffect(() => {
    if (onStrengthChange) onStrengthChange(strength);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strength.score, strength.label]); // avoid firing too often

  if (!password) return null;

  return (
    <Box sx={{ mt: 1.5, mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Password strength: {strength.label}
        </Typography>
        <Typography variant="caption" sx={{ color: strengthColor, fontWeight: 600 }}>
          {Math.round(strengthPercent)}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={strengthPercent}
        sx={{
          height: 8,
          borderRadius: 6,
          backgroundColor: theme.palette.action.disabledBackground,
          "& .MuiLinearProgress-bar": {
            backgroundColor: strengthColor,
            borderRadius: 6,
          },
        }}
      />
      {showSuggestions && strength.suggestions.length > 0 && (
        <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {strength.suggestions.slice(0, maxSuggestions).map((sug, idx) => (
            <Chip key={idx} size="small" label={sug} sx={{ opacity: 0.85 }} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PasswordStrengthMeter;


